/**
 * Entity workbench — Relationships tab (M5c surface, 2026-09-24).
 *
 * Who holds this company (dated substantial holdings, each backed by its
 * 603/604/605 filings and the associates the holder filed with), the
 * directors' reported interests (3X/3Y/3Z — flagging anyone who has left the
 * board but still reports a holding), what this company itself holds, who acts
 * for it and who works there — and the holder filings that did not resolve,
 * so a gap is visible rather than read as "no holders".
 *
 * ACCOUNTABLE surface: every figure carries its filing, its date and how it
 * was resolved. PERSON_CONTACT_UNIFICATION_DESIGN_2026-09-22.md §4e.
 */
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  AlertTriangle, Briefcase, ChevronDown, ChevronRight, FileText, Landmark, Loader2,
  PieChart, UserCheck, Users,
} from 'lucide-react';
import {
  getCynapseEntityRelationships, getEntityRelationships,
  type DirectorInterest, type HoldingAssociate, type HoldingGroup, type RelationshipEdge,
  type UnresolvedHolderFiling,
} from '../../lib/api/entity-workbench.js';
import { Modal, ModalBody, ModalHeader } from '../ui/modal.js';
import { TableScroll } from '../ui/table-scroll.js';

const FORM_LABEL: Record<string, string> = {
  form_603: '603 · becoming',
  form_604: '604 · change',
  form_605: '605 · ceasing',
};

const NOTICE_LABEL: Record<string, string> = {
  initial: '3X · on appointment',
  change: '3Y · change',
  final: '3Z · on leaving',
};

const TRADE_LABEL: Record<string, string> = {
  buy: 'Buy', sell: 'Sell', grant: 'Issue / grant', exercise: 'Exercise',
  lapse: 'Lapse', other: 'Other',
};

const ACTS_FOR_LABEL: Record<string, string> = {
  advisor_to: 'Adviser', lead_manager_of: 'Lead manager', underwriter_of: 'Underwriter',
  auditor_of: 'Auditor', jv_partner_of: 'JV partner', customer_of: 'Customer',
};

const KIND_LABEL: Record<string, string> = {
  transaction: 'transaction', financial: 'financial', legal: 'legal', ir_pr: 'IR / PR',
  technical: 'technical', registry: 'registry', secretarial: 'secretarial', audit: 'audit',
};

const RESOLUTION_LABEL: Record<string, string> = {
  unmatched: 'not matched', ambiguous: 'several candidates', no_issuer: 'issuer not linked',
  unresolvable: 'not a name', resolved: 'matched', created: 'new record',
};

function fmtDate(d: string | null | undefined) {
  return d ? format(new Date(d), 'd MMM yyyy') : '—';
}
function fmtPct(p: number | null | undefined) {
  return p == null ? '—' : `${Number(p).toFixed(2)}%`;
}
function fmtNum(n: number | null | undefined) {
  return n == null ? '—' : Math.round(n).toLocaleString('en-AU');
}
function fmtAud(n: number | null | undefined) {
  return n == null ? '—' : `$${Math.round(n).toLocaleString('en-AU')}`;
}

function Panel({ icon: Icon, title, meta, children }: {
  icon: typeof Users; title: string; meta?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-2xl border border-grey-100 p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-grey-900 flex items-center gap-2">
          <Icon className="h-4 w-4 text-grey-400" /> {title}
        </h3>
        {meta && <div className="text-xs text-grey-500">{meta}</div>}
      </div>
      {children}
    </section>
  );
}

function Segmented<T extends string>({ value, options, onChange }: {
  value: T; options: { key: T; label: string }[]; onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-full border border-grey-200 p-0.5 text-xs">
      {options.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`px-3 py-1 rounded-full ${value === o.key ? 'bg-grey-900 text-white' : 'text-grey-600 hover:text-grey-900'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function RelationshipsTab({ entityId }: { entityId: string }) {
  const [holderDrawer, setHolderDrawer] = useState<{ id: string; name: string } | null>(null);
  const query = useQuery({
    queryKey: ['entity-relationships', entityId],
    queryFn: () => getEntityRelationships(entityId),
  });

  if (query.isLoading) {
    return <div className="py-16 text-center text-sm text-grey-500">Loading relationships…</div>;
  }
  if (query.isError || !query.data) {
    return <div className="py-16 text-center text-sm text-red-600">Could not load relationships.</div>;
  }
  const d = query.data;
  const openHolder = (g: { entity_id: string | null; name: string }) =>
    g.entity_id && setHolderDrawer({ id: g.entity_id, name: g.name });

  return (
    <div className="space-y-4">
      {!d.cynapse_entity_id && (
        <Notice>
          This entity is not linked to Cynapse, so holdings and relationships cannot be read. Director
          interests below come from its own filings.
        </Notice>
      )}
      {d.cynapse_error && (
        <Notice>Cynapse did not answer ({d.cynapse_error}); holdings and relationships may be missing.</Notice>
      )}

      <HoldersPanel
        title="Substantial holders"
        icon={PieChart}
        groups={d.holders}
        counterpartLabel="Holder"
        asOf={d.as_of}
        onOpen={openHolder}
      />
      <DirectorsPanel directors={d.director_interests} />
      {d.holds.length > 0 && (
        <HoldersPanel
          title="Holdings in other companies"
          icon={Landmark}
          groups={d.holds}
          counterpartLabel="Company"
          asOf={d.as_of}
          onOpen={openHolder}
        />
      )}
      <ActsForPanel edges={d.acts_for} windowDays={d.window_days} />
      <PeoplePanel people={d.people} windowDays={d.window_days} />
      <UnresolvedPanel rows={d.unresolved_holders} />

      {holderDrawer && (
        <HolderDrawer
          cynapseId={holderDrawer.id}
          name={holderDrawer.name}
          onClose={() => setHolderDrawer(null)}
        />
      )}
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <div>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Substantial holders (and the entity's own holdings — same shape)
// ---------------------------------------------------------------------------

function HoldersPanel({ title, icon, groups, counterpartLabel, asOf, onOpen }: {
  title: string; icon: typeof Users; groups: HoldingGroup[]; counterpartLabel: string;
  asOf: string | null; onOpen: (g: HoldingGroup) => void;
}) {
  const [filter, setFilter] = useState<'current' | 'past' | 'all'>('current');
  const [open, setOpen] = useState<string | null>(null);
  const shown = groups.filter(g => filter === 'all' || (filter === 'current' ? g.current : !g.current));
  const nCurrent = groups.filter(g => g.current).length;

  return (
    <Panel
      icon={icon}
      title={title}
      meta={<>{nCurrent} current · {groups.length - nCurrent} past · as at {fmtDate(asOf)}</>}
    >
      <Segmented<'current' | 'past' | 'all'>
        value={filter}
        onChange={setFilter}
        options={[{ key: 'current', label: 'Current' }, { key: 'past', label: 'Past' }, { key: 'all', label: 'All' }]}
      />
      {shown.length === 0 ? (
        <p className="text-sm text-grey-500">
          {groups.length === 0 ? 'No substantial-holder filings recorded.' : 'None in this view.'}
        </p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-grey-500 border-b border-grey-100">
                <th className="py-2 pr-3 w-6" />
                <th className="py-2 pr-3">{counterpartLabel}</th>
                <th className="py-2 pr-3 text-right">Voting power</th>
                <th className="py-2 pr-3">Since</th>
                <th className="py-2 pr-3">Ceased</th>
                <th className="py-2 pr-3 text-right">Filings</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(g => {
                const key = g.entity_id ?? g.name;
                const isOpen = open === key;
                const filings = g.history.reduce((n, h) => n + h.filings.length, 0);
                return (
                  <Fragment key={key}>
                    <tr className="border-b border-grey-50 hover:bg-grey-50">
                      <td className="py-2 pr-3">
                        <button
                          onClick={() => setOpen(isOpen ? null : key)}
                          aria-label={isOpen ? 'Hide history' : 'Show history'}
                          className="text-grey-400 hover:text-grey-700"
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        <CounterpartName g={g} onOpen={() => onOpen(g)} />
                        <span className="ml-2 text-xs text-grey-400">{g.entity_type}</span>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{fmtPct(g.pct)}</td>
                      <td className="py-2 pr-3">{fmtDate(g.since)}</td>
                      <td className="py-2 pr-3">{g.current ? <span className="text-emerald-700">holding</span> : fmtDate(g.ceased)}</td>
                      <td className="py-2 pr-3 text-right text-grey-500">{filings}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-grey-50/60">
                        <td />
                        <td colSpan={5} className="py-3 pr-3">
                          <HoldingHistory g={g} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

function CounterpartName({ g, onOpen }: { g: { assayer_entity_id?: string | null; entity_id: string | null; name: string }; onOpen: () => void }) {
  if (g.assayer_entity_id) {
    return (
      <Link to={`/entities/${g.assayer_entity_id}?tab=relationships`} className="font-medium text-primary-700 hover:underline">
        {g.name}
      </Link>
    );
  }
  if (g.entity_id) {
    return (
      <button onClick={onOpen} className="font-medium text-primary-700 hover:underline text-left">
        {g.name}
      </button>
    );
  }
  return <span className="font-medium text-grey-900">{g.name}</span>;
}

function HoldingHistory({ g }: { g: HoldingGroup }) {
  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {g.history.map((h, i) => (
          <li key={i} className="text-sm">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-mono">{fmtPct(h.pct)}</span>
              <span className="text-grey-600">
                {h.start_unknown ? 'held by ' : 'from '}{fmtDate(h.valid_from)}
                {h.valid_to ? ` to ${fmtDate(h.valid_to)}` : ' — current'}
              </span>
              {h.start_unknown && (
                <span className="text-xs text-grey-400" title="The first filing we hold for this holding is a change or ceasing notice, so when it began is not known.">
                  start not on file
                </span>
              )}
            </div>
            <ul className="mt-1 ml-3 space-y-0.5">
              {h.filings.map(f => (
                <li key={f.filing_id} className="text-xs text-grey-600 flex flex-wrap gap-x-2">
                  <FileText className="h-3 w-3 mt-0.5 text-grey-400" />
                  <span>{f.form_type ? FORM_LABEL[f.form_type] ?? f.form_type : f.transaction_type}</span>
                  <span>{fmtDate(f.transaction_date ?? f.announcement_date)}</span>
                  {f.previous_pct != null && <span className="font-mono">{fmtPct(f.previous_pct)} →</span>}
                  {f.current_pct != null && <span className="font-mono">{fmtPct(f.current_pct)}</span>}
                  {f.announcement_id ? (
                    <Link to={`/announcements/${f.announcement_id}`} className="text-primary-700 hover:underline truncate max-w-md">
                      {f.announcement_title ?? 'filing'}
                    </Link>
                  ) : (
                    <span className="text-grey-400">title only — document not fetched</span>
                  )}
                  <span className="text-grey-400">as filed: “{f.filer_name_raw}”</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      {g.associates.length > 0 && <Associates associates={g.associates} />}
    </div>
  );
}

function Associates({ associates }: { associates: HoldingAssociate[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-grey-500 uppercase mb-1">
        Filed together with ({associates.length})
      </h4>
      <ul className="flex flex-wrap gap-1.5">
        {associates.map((a, i) => (
          <li
            key={`${a.name}-${i}`}
            title={[a.association_type.replace(/_/g, ' '), a.role_label, a.entity_id ? 'matched to a record' : 'not matched to a record']
              .filter(Boolean).join(' · ')}
            className={`px-2 py-0.5 rounded-full text-xs border ${a.entity_id ? 'border-primary-200 text-primary-800 bg-primary-50' : 'border-grey-200 text-grey-600'}`}
          >
            {a.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Director interests (3X / 3Y / 3Z)
// ---------------------------------------------------------------------------

function DirectorsPanel({ directors }: { directors: DirectorInterest[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const leftButHolds = directors.filter(d => d.left_but_holds).length;
  return (
    <Panel
      icon={UserCheck}
      title="Director interests"
      meta={<>{directors.length} directors with notices{leftButHolds ? ` · ${leftButHolds} left the board, still holding` : ''}</>}
    >
      {directors.length === 0 ? (
        <p className="text-sm text-grey-500">No director's interest notices recorded.</p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-grey-500 border-b border-grey-100">
                <th className="py-2 pr-3 w-6" />
                <th className="py-2 pr-3">Director</th>
                <th className="py-2 pr-3">Board</th>
                <th className="py-2 pr-3 text-right">Holding (latest notice)</th>
                <th className="py-2 pr-3">Latest notice</th>
              </tr>
            </thead>
            <tbody>
              {directors.map(d => {
                const key = d.person_entity_id ?? d.name;
                const isOpen = open === key;
                const latest = d.notices[0];
                return (
                  <Fragment key={key}>
                    <tr className="border-b border-grey-50 hover:bg-grey-50">
                      <td className="py-2 pr-3">
                        <button
                          onClick={() => setOpen(isOpen ? null : key)}
                          aria-label={isOpen ? 'Hide notices' : 'Show notices'}
                          className="text-grey-400 hover:text-grey-700"
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        <span className="font-medium text-grey-900">{d.name}</span>
                        {!d.person_entity_id && (
                          <span className="ml-2 text-xs text-grey-400" title="Not matched to a person record">
                            {RESOLUTION_LABEL[d.resolution_status ?? ''] ?? 'not matched'}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <BoardStatus d={d} />
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{fmtNum(d.latest_holding)}</td>
                      <td className="py-2 pr-3 text-grey-600">
                        {latest?.notice_type ? NOTICE_LABEL[latest.notice_type] : TRADE_LABEL[latest?.trade_type] ?? '—'}
                        <span className="ml-2">{fmtDate(d.latest_date)}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-grey-50/60">
                        <td />
                        <td colSpan={4} className="py-3 pr-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-grey-500">
                                <th className="py-1 pr-3">Date</th>
                                <th className="py-1 pr-3">Notice</th>
                                <th className="py-1 pr-3">Change</th>
                                <th className="py-1 pr-3 text-right">Securities</th>
                                <th className="py-1 pr-3 text-right">Value</th>
                                <th className="py-1 pr-3 text-right">Held after</th>
                                <th className="py-1 pr-3">Source</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.notices.map(n => (
                                <tr key={n.trade_id} className="border-t border-grey-100">
                                  <td className="py-1 pr-3">{fmtDate(n.trade_date)}</td>
                                  <td className="py-1 pr-3">{n.notice_type ? NOTICE_LABEL[n.notice_type] : '—'}</td>
                                  <td className="py-1 pr-3">
                                    {TRADE_LABEL[n.trade_type] ?? n.trade_type}
                                    {n.on_market && <span className="text-grey-400"> · on market</span>}
                                  </td>
                                  <td className="py-1 pr-3 text-right font-mono">{fmtNum(n.shares_changed)}</td>
                                  <td className="py-1 pr-3 text-right font-mono">{fmtAud(n.trade_value_aud)}</td>
                                  <td className="py-1 pr-3 text-right font-mono">{fmtNum(n.holding_after)}</td>
                                  <td className="py-1 pr-3">
                                    {n.announcement_id ? (
                                      <Link to={`/announcements/${n.announcement_id}`} className="text-primary-700 hover:underline">
                                        filing
                                      </Link>
                                    ) : (
                                      <span className="text-grey-400">title only</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

function BoardStatus({ d }: { d: DirectorInterest }) {
  if (d.board_status === 'current') {
    return <span className="text-emerald-700">on the board</span>;
  }
  if (d.board_status === 'former') {
    return (
      <span className={d.left_but_holds ? 'text-amber-700 font-medium' : 'text-grey-600'}>
        left the board{d.left_but_holds ? ' — still holds' : ''}
      </span>
    );
  }
  return <span className="text-grey-400" title="No board role on record for this person at this company">not on record</span>;
}

// ---------------------------------------------------------------------------
// Acts for this company · People
// ---------------------------------------------------------------------------

function ActsForPanel({ edges, windowDays }: { edges: RelationshipEdge[]; windowDays: number }) {
  const [filter, setFilter] = useState<'current' | 'all'>('current');
  const shown = useMemo(
    () => edges.filter(e => filter === 'all' || e.current)
      .sort((a, b) => (a.relationship_type + a.name).localeCompare(b.relationship_type + b.name)),
    [edges, filter],
  );
  return (
    <Panel
      icon={Briefcase}
      title="Acts for this company"
      meta={<>current = seen on a filing in the last {Math.round(windowDays / 30)} months</>}
    >
      <Segmented<'current' | 'all'> value={filter} onChange={setFilter} options={[{ key: 'current', label: 'Current' }, { key: 'all', label: 'All' }]} />
      {shown.length === 0 ? (
        <p className="text-sm text-grey-500">No advisers, managers or partners recorded{filter === 'current' ? ' in the window' : ''}.</p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-grey-500 border-b border-grey-100">
                <th className="py-2 pr-3">Firm</th>
                <th className="py-2 pr-3">As</th>
                <th className="py-2 pr-3">First seen</th>
                <th className="py-2 pr-3">Last seen</th>
                <th className="py-2 pr-3 text-right">Filings</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(e => (
                <tr key={`${e.relationship_type}-${e.entity_id}`} className="border-b border-grey-50">
                  <td className="py-2 pr-3">
                    {e.assayer_entity_id ? (
                      <Link to={`/entities/${e.assayer_entity_id}?tab=relationships`} className="font-medium text-primary-700 hover:underline">{e.name}</Link>
                    ) : <span className="font-medium text-grey-900">{e.name}</span>}
                  </td>
                  <td className="py-2 pr-3 text-grey-600">
                    {ACTS_FOR_LABEL[e.relationship_type] ?? e.relationship_type}
                    {e.kind && <span className="text-grey-400"> · {KIND_LABEL[e.kind] ?? e.kind}</span>}
                  </td>
                  <td className="py-2 pr-3">{fmtDate(e.first_seen)}</td>
                  <td className="py-2 pr-3">{fmtDate(e.last_seen)}</td>
                  <td className="py-2 pr-3 text-right text-grey-500">{e.sightings ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Panel>
  );
}

function PeoplePanel({ people, windowDays }: { people: RelationshipEdge[]; windowDays: number }) {
  const current = people.filter(p => p.current);
  const shown = current.length ? current : people;
  return (
    <Panel
      icon={Users}
      title="People working here"
      meta={<>{current.length} seen on filings in the last {Math.round(windowDays / 30)} months · {people.length} ever</>}
    >
      {people.length === 0 ? (
        <p className="text-sm text-grey-500">No staff recorded from filing contact lines.</p>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-sm">
          {shown.sort((a, b) => a.name.localeCompare(b.name)).map(p => (
            <li key={p.entity_id} className="flex justify-between gap-2">
              <span className="text-grey-900 truncate">{p.name}</span>
              <span className="text-xs text-grey-400 shrink-0">{fmtDate(p.last_seen)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Holder filings that did not resolve — visible, never silently dropped
// ---------------------------------------------------------------------------

function UnresolvedPanel({ rows }: { rows: UnresolvedHolderFiling[] }) {
  if (rows.length === 0) return null;
  return (
    <Panel icon={AlertTriangle} title="Holder filings not matched to a holder" meta={<>{rows.length} filings</>}>
      <p className="text-xs text-grey-500">
        These filings are recorded but their holder is not linked to a record, so they are not in the holders above.
      </p>
      <TableScroll>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-grey-500 border-b border-grey-100">
              <th className="py-2 pr-3">Holder as filed</th>
              <th className="py-2 pr-3">Why</th>
              <th className="py-2 pr-3">Notice</th>
              <th className="py-2 pr-3 text-right">Voting power</th>
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.filing_id} className="border-b border-grey-50">
                <td className="py-2 pr-3 text-grey-900">{r.filer_name_raw}</td>
                <td className="py-2 pr-3 text-grey-600">{RESOLUTION_LABEL[r.filer_resolution_status ?? ''] ?? 'not yet processed'}</td>
                <td className="py-2 pr-3 text-grey-600">{r.form_type ? FORM_LABEL[r.form_type] ?? r.form_type : r.transaction_type}</td>
                <td className="py-2 pr-3 text-right font-mono">{fmtPct(r.current_pct)}</td>
                <td className="py-2 pr-3">{fmtDate(r.on_date)}</td>
                <td className="py-2 pr-3">
                  {r.announcement_id ? (
                    <Link to={`/announcements/${r.announcement_id}`} className="text-primary-700 hover:underline">filing</Link>
                  ) : <span className="text-grey-400">title only</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// A holder assayer does not track — everything it holds, from Cynapse
// ---------------------------------------------------------------------------

function HolderDrawer({ cynapseId, name, onClose }: { cynapseId: string; name: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['cynapse-entity-relationships', cynapseId],
    queryFn: () => getCynapseEntityRelationships(cynapseId),
  });
  const holdings = useMemo(() => {
    const rows = (query.data?.outgoing ?? []).filter(e => e.relationship_type === 'substantial_holder_of' && !e.withdrawn);
    const by = new Map<string, RelationshipEdge[]>();
    rows.forEach(r => by.set(r.entity_id, [...(by.get(r.entity_id) ?? []), r]));
    return Array.from(by.values()).map(rs => {
      rs.sort((a, b) => (b.valid_from ?? '').localeCompare(a.valid_from ?? ''));
      return { latest: rs[0], current: rs.some(r => r.current), periods: rs.length };
    }).sort((a, b) => Number(b.current) - Number(a.current) || (b.latest.pct ?? 0) - (a.latest.pct ?? 0));
  }, [query.data]);
  const associates = (query.data?.incoming ?? []).filter(e => e.relationship_type === 'associate_of');

  return (
    <Modal onClose={onClose} size="3xl" closeOnBackdrop>
      <ModalHeader>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-grey-900 truncate">{name}</h2>
          <p className="text-xs text-grey-500">Substantial holdings across companies · from filed 603/604/605 notices</p>
        </div>
      </ModalHeader>
      <ModalBody>
        {query.isLoading && (
          <div className="py-10 text-center text-sm text-grey-500"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>
        )}
        {query.isError && <div className="py-10 text-center text-sm text-red-600">Could not load this holder.</div>}
        {query.data && (
          <div className="space-y-4">
            {holdings.length === 0 ? (
              <p className="text-sm text-grey-500">No holdings recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-grey-500 border-b border-grey-100">
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3 text-right">Voting power</th>
                    <th className="py-2 pr-3">Since</th>
                    <th className="py-2 pr-3">Ceased</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.map(({ latest, current }) => (
                    <tr key={latest.entity_id} className="border-b border-grey-50">
                      <td className="py-2 pr-3 font-medium text-grey-900">{latest.name}</td>
                      <td className="py-2 pr-3 text-right font-mono">{fmtPct(latest.pct)}</td>
                      <td className="py-2 pr-3">{fmtDate(latest.valid_from ?? latest.first_seen)}</td>
                      <td className="py-2 pr-3">{current ? <span className="text-emerald-700">holding</span> : fmtDate(latest.valid_to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {associates.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-grey-500 uppercase mb-1">Named as its associates</h4>
                <ul className="flex flex-wrap gap-1.5">
                  {associates.map(a => (
                    <li key={a.entity_id} className="px-2 py-0.5 rounded-full text-xs border border-grey-200 text-grey-700">
                      {a.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}
